import { cn } from '@/lib/utils';
import type { Transcript } from './api';

// m:ss (h:mm:ss past an hour) for a transcript segment's start offset. Unlike
// formatDuration, 0 is a real timestamp (the call's start), not "no data" — so this
// never renders "—".
export function formatOffset(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return `${m}:${String(s).padStart(2, '0')}`;
}

// A stable per-speaker text color so the two parties are visually distinct in the
// transcript. Assigned by first-appearance order (cycled if a diarizer ever emits
// more labels than the palette). Palette classes match those already used elsewhere
// on the board (see the amber/emerald/indigo usages), so they survive Tailwind purge.
const SPEAKER_COLORS = [
  'text-sky-700',
  'text-emerald-700',
  'text-violet-700',
  'text-amber-700',
] as const;

// The diarized conversation for one call, rendered inside the transcript modal. Each
// segment is one turn: a left rail with the (color-coded) speaker + start offset, and
// the spoken text (wrapping) beside it. The scroll container is the modal body around
// it, so this just lays out the full list. An empty conversation shows an explicit note
// rather than a blank panel (a real call that produced no speech, e.g. immediate
// hangup, still has an envelope).
export function TranscriptPanel({ transcript }: { transcript: Transcript }) {
  const segments = transcript.conversation ?? [];
  if (segments.length === 0) {
    return (
      <p className="text-muted-foreground py-2 text-center text-xs italic">
        No speech was transcribed for this call.
      </p>
    );
  }
  // First-appearance order -> stable color index per speaker label.
  const order = Array.from(new Set(segments.map((s) => s.speaker)));
  const colorFor = (speaker: string): string =>
    SPEAKER_COLORS[order.indexOf(speaker) % SPEAKER_COLORS.length] ?? 'text-foreground';

  return (
    <ol className="space-y-1.5 pr-1">
      {segments.map((seg, i) => (
        <li key={i} className="flex gap-3">
          <span className="w-24 flex-none font-mono text-[0.68rem] leading-relaxed tabular-nums">
            <span className={cn('font-semibold', colorFor(seg.speaker))}>{seg.speaker}</span>
            <span className="text-muted-foreground block">{formatOffset(seg.startSec)}</span>
          </span>
          <span className="min-w-0 flex-1 whitespace-pre-wrap leading-relaxed">{seg.text}</span>
        </li>
      ))}
    </ol>
  );
}
